import { ERROR_MESSAGE, LIMIT, LOAD_MESSAGE, PAGE_PATH, SUCCESS_MESSAGE } from '@/constants';
import {
  fetchCancelOrder,
  fetchCompRecords,
  fetchConfirmReceiptOfGoods,
  fetchCreateWxChatPaymentIntent,
  fetchDeleteOrder,
  fetchFreeGiftByOrderId,
  fetchMerchantVoluntaryRefundByOrder,
  fetchOrderDetails,
  fetchOrderList,
  fetchOrderLogistics,
  fetchOrderPaymentRewardCoupons,
  fetchOrderPromotionResult,
  fetchRemoveAllOrderByRegularOrder,
  fetchUpdateOrderShippingAddress,
  uniPayment,
} from '@/services';
import { useAddressStore, useAppStore, usePlaceOrderStore } from '@/store';
import {
  FetchAbnormalCodes,
  LoadMoreStatus,
  OrderState,
  PlaceOrderType,
  PromotionType,
  type AnyObj,
  type AnyObjOrNull,
} from '@/types';
import { businessUtils, commonUtils, toast } from '@/utils';
import { defineStore } from 'pinia';
import NP from 'number-precision';

interface State {
  // 订单id
  orderId: string;
  /** 订单列表 */
  orderList: AnyObj[];
  /** 标签栏下标 */
  tabIndex: number;
  /** 列表请求选项 */
  options: { filterState: AnyObj; skip: number };
  /** 列表加载状态 */
  loadStatus: LoadMoreStatus;
  /** 订单详情 */
  orderDetails: AnyObjOrNull;
  /** 订单关联的礼品数据 */
  orderCompRecords: AnyObj[];
  /** 提示类弹窗配置信息 */
  promptPopupConfigs: {
    ref: AnyObjOrNull;
    message: string;
    confirmFn: Function;
  };
  /** 订单物流数据 */
  logisticsData: AnyObjOrNull;
  /** 订单优惠活动数据 */
  orderPromotionResult: AnyObjOrNull;
  /** 当前订单的所有赠品 */
  freeGiftByOrder: AnyObj[];
  /** 商家主动退款数据 */
  merchantVoluntaryRefundByOrder: AnyObj[];
  /** 支付节流表示 */
  canPay: boolean;
  /** 支付有礼优惠券数据 */
  orderPaymentRewardCoupons: AnyObject[];
  /** 支付有礼优惠券适用商品数据 */
  orderPaymentRewardCouponsCommoditys: AnyObject[];
  /** 盲盒支付价 */
  blindBoxPayed: number;
}

export const useOrdersStore = defineStore('orders', {
  state: (): State => ({
    orderId: '',
    orderList: [],
    tabIndex: 0,
    options: {
      filterState: {
        // contains: '',
        notIn: [OrderState.AddingItems, OrderState.DelayDeliver],
      },
      skip: 0,
    },
    loadStatus: LoadMoreStatus.loading,
    orderDetails: null,
    orderCompRecords: [],
    promptPopupConfigs: {
      ref: null,
      message: '',
      confirmFn: () => {},
    },
    logisticsData: null,
    orderPromotionResult: null,
    freeGiftByOrder: [],
    merchantVoluntaryRefundByOrder: [],
    canPay: true,
    orderPaymentRewardCoupons: [],
    orderPaymentRewardCouponsCommoditys: [],
    blindBoxPayed: 0,
  }),

  getters: {
    /** 是否多期订阅 */
    isSubscribe(state) {
      return !!state.orderDetails?.customFields?.subscription?.id;
    },
    /** 当前订单关联的礼品数据 */
    orderCompRecord(state) {
      return state.orderCompRecords?.[0];
    },
    /** 优惠类型详情 */
    discountByTypesList(state): AnyObj[] {
      return businessUtils.placeOrder.getDiscounts(
        state.orderPromotionResult?.promResult?.discountByTypes ?? []
      );
    },
    freeGiftList(state) {
      if (state.orderPromotionResult && !!state.freeGiftByOrder?.length) {
        const list = useOrdersStore().freeGiftHandler(
          state.orderPromotionResult.promResult,
          state.freeGiftByOrder
        );
        if (list) {
          return list
            .map((it) => it.items)
            .flat()
            .filter((it) => !!it?.selected);
        }
      }
      return [];
    },
    /** 盲盒支付价 */
    blindBoxPayed(state) {
      const promLineResults = state.orderPromotionResult?.promResult?.promLineResults ?? [];
      const target = promLineResults.find((it: AnyObject) => it.type === PromotionType.BlindBox);
      if (target) {
        let sum = 0;
        target.orderLineBlindBoxMap?.forEach((it: AnyObject) => {
          sum += it.blindBoxOrderBuyPrice;
        });
        return sum;
      }
      return 0;
    },
    /** 购物金数据 */
    shoppingGold(): {
      total: number;
      shoppingCreditsDeduction: number;
      shoppingCreditsClaim: number;
    } {
      const promResult = this.orderPromotionResult?.promResult;
      // 购物金抵扣
      const shoppingCreditsDeduction = promResult?.shoppingCreditsDeduction ?? 0;
      // 可得购物金
      const shoppingCreditsClaim = promResult?.shoppingCreditsClaim ?? 0;
      // 总金额
      const total = NP.plus(promResult?.orderTotalPrice ?? 0, shoppingCreditsDeduction);

      console.log(`购物金数据`, {
        total,
        shoppingCreditsClaim,
        shoppingCreditsDeduction,
      });

      return {
        total,
        shoppingCreditsClaim,
        shoppingCreditsDeduction,
      };
    },
  },

  actions: {
    /**
     * 取消订单
     * @param orderId - 订单id
     */
    async cancelOrder(orderId: string, callback?: Function) {
      const res = await fetchCancelOrder(orderId);
      if (res) {
        const index = this.orderList.findIndex((it) => it.id === orderId);
        index >= 0 && (this.orderList[index] = { ...res });

        await callback?.();

        toast.message('已取消');
      }
    },
    /**
     * 继续支付
     * @param orderId - 订单id
     */
    async continuePay(orderId: string, callback?: Function) {
      let cancelLoading;
      try {
        if (this.canPay) {
          this.canPay = false;

          cancelLoading = toast.loading(LOAD_MESSAGE.payment);

          const wxRes = await fetchCreateWxChatPaymentIntent({ orderId });

          cancelLoading();

          const payRes = await uniPayment(wxRes);

          let data;

          if (payRes) {
            cancelLoading = toast.loading();

            // 支付成功，根据订单号查询订单详情数据
            data = await commonUtils.data.fetchWithRetry(
              // 查询函数
              (orderId: string) => fetchOrderDetails(orderId),
              // 查询参数
              orderId,
              // 验证函数
              (result: any) => result?.state === OrderState.PaymentSettled
            );

            cancelLoading();
          }

          if (data) {
            const index = this.orderList.findIndex((it) => it.id === orderId);
            index >= 0 && (this.orderList[index] = { ...data });

            toast.message(SUCCESS_MESSAGE.payment);

            await callback?.();
          } else {
            toast.message(ERROR_MESSAGE.payment);
          }

          this.canPay = true;

          useAppStore().noticcNotPaymentCompleteFlag = true;
        }
      } catch (error) {
        console.log(`继续支付`, error);
        this.canPay = true;
        cancelLoading?.();
        const e = error as AnyObject;
        if (e?.abnormalCode === FetchAbnormalCodes.PRODUCT_REGION_RESTRICTION_ERROR) {
          toast.message(e.err.message);
        }
      }
    },
    /**
     * 删除订单
     * @param orderId - 订单id
     */
    async deleteOrder(orderId: string, callback?: Function) {
      const res = await fetchDeleteOrder(orderId);
      if (res) {
        toast.message('删除成功', () => {
          const index = this.orderList.findIndex((it) => it.id === orderId);
          index >= 0 && this.orderList.splice(index, 1);
          callback?.();
        });
      }
    },
    /**
     * 确认收货
     * @param orderId - 订单id
     */
    async confirmReceiptOfGoods(orderId: string, callback?: Function) {
      const res = await fetchConfirmReceiptOfGoods(orderId);
      if (res) {
        const index = this.orderList.findIndex((it) => it.id === orderId);
        index >= 0 && this.orderList.splice(index, 1);

        await callback?.();

        toast.message('已确认收货');
      }
    },
    /**
     * 再次购买
     * @param item - 订单项
     */
    async buyAgain(item: AnyObj) {
      const its = item.lines.map((it: AnyObj) => ({
        productVariantId: it.productVariant.id,
        quantity: it.quantity || it.items.length,
      }));

      const removeRes = await fetchRemoveAllOrderByRegularOrder();
      if (removeRes) {
        // 依次加入订单
        await its.forEach(async (it: AnyObj) => {
          await businessUtils.placeOrder.matomoAddItemToOrderByOrderType(
            it,
            PlaceOrderType.regularOrder
          );
        });

        const placeOrderStore = usePlaceOrderStore();

        placeOrderStore.paySuccessCallback = async () => {
          await this.getOrderList(0, true);
        };

        placeOrderStore.placeOrderType = PlaceOrderType.regularOrder;

        uni.navigateTo({ url: PAGE_PATH.orderConfirm.route });
      }
    },
    /**
     * 查看物流
     * @param orderId - 订单id
     */
    lookupLogistics(orderId: string) {
      uni.navigateTo({ url: `${PAGE_PATH.orderLogistics.route}?id=${orderId}` });
    },
    /**
     * 修改地址
     * @param orderId - 订单id
     */
    modifyShipAddress(orders: AnyObjOrNull, orderId: string, callback?: Function) {
      const addressStore = useAddressStore();

      addressStore.displayedAddress = orders?.shippingAddress;

      const selectedAfterCallback = async (selected: AnyObj) => {
        const input = {
          city: selected.city,
          company: selected.company,
          countryCode: 'CN',
          fullName: selected.fullName,
          phoneNumber: selected.phoneNumber,
          postalCode: selected.postalCode,
          province: selected.province,
          streetLine1: selected.streetLine1,
          streetLine2: selected.streetLine2,
          customFields: {
            district: selected.customFields.district,
          },
        };

        const res = await fetchUpdateOrderShippingAddress({
          orderId,
          input,
        }).catch((error) => {
          console.log(`修改地址`, error);

          addressStore.selectedAfterCallback = null;

          const e = error as AnyObject;
          if (e?.abnormalCode === FetchAbnormalCodes.PRODUCT_REGION_RESTRICTION_ERROR) {
            toast.message(e.err.message);
          }
        });

        if (res) {
          addressStore.selectedAfterCallback = null;

          await callback?.();

          // 更新列表数据和详情数据
          this.updateListItemAndDetails(orderId);

          toast.message('修改成功');
        }
      };

      addressStore.selectedAfterCallback = selectedAfterCallback;

      uni.navigateTo({ url: PAGE_PATH.addressModify.route });
    },
    async getOrderList(skip: number = 0, refresh?: boolean) {
      const res = await fetchOrderList(skip, this.options.filterState);
      if (res) {
        if (res.length < LIMIT) {
          this.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.orderList = res;
        } else {
          this.orderList = [...this.orderList, ...res];
        }
      }
      return this.orderList;
    },
    async getOrderDetails(orderId: string, refresh?: boolean) {
      if (!this.orderDetails || refresh) {
        this.orderDetails = await fetchOrderDetails(orderId);
      }
      return this.orderDetails;
    },
    async getOrderPromotionResult(orderId: string) {
      const data = await fetchOrderPromotionResult([orderId]);
      if (data?.length) {
        this.orderPromotionResult = data[0];
        if (this.orderPromotionResult?.promResult?.gifts?.length) {
          this.getFreeGiftByOrder(orderId, true);
        }
      }
      return this.orderPromotionResult;
    },
    async getFreeGiftByOrder(orderId: string, refresh?: boolean) {
      if (!this.freeGiftByOrder?.length || refresh) {
        const data = await fetchFreeGiftByOrderId(orderId);
        this.freeGiftByOrder = data;
      }
    },
    async getOrderPaymentRewardCoupons(orderId: string, refresh?: boolean) {
      try {
        if (!this.orderPaymentRewardCoupons.length || refresh) {
          const res = await fetchOrderPaymentRewardCoupons(orderId);
          if (res.items?.length) {
            const list = res.items.map((it: AnyObject) => {
              it._couponConfig = businessUtils.coupon.getConfig(it.coupon);
              return it;
            });
            this.orderPaymentRewardCoupons = list;
          } else {
            this.orderPaymentRewardCoupons = [];
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    async getOrderPaymentRewardCouponsCommoditys(refresh?: boolean) {
      try {
        const commoditys = [];

        if (!this.orderPaymentRewardCouponsCommoditys.length || refresh) {
          for (let i = 0; i < this.orderPaymentRewardCoupons.length; i++) {
            const item = this.orderPaymentRewardCoupons[i];
            const ap = item.coupon.applicableProduct;

            const products = await businessUtils.activity.getApplicableProducts(
              ap.applicableType,
              // ap.productIds.slice(0, 3)
              ap.productIds,
              3
            );

            commoditys.push(products);
          }
          this.orderPaymentRewardCouponsCommoditys = commoditys;
        }

        return this.orderPaymentRewardCouponsCommoditys;
      } catch (error) {
        console.log(error);
      }
    },
    async getCompRecords(orderId: string = '') {
      if (commonUtils.func.isEmpty(this.orderCompRecords)) {
        await fetchCompRecords(orderId);
      }
      return this.orderCompRecords;
    },
    async getOrderLogistics(orderId: string, refresh?: boolean) {
      if (!this.logisticsData || refresh) {
        const data = await fetchOrderLogistics(orderId);
        if (data) this.logisticsData = data;
      }
      return this.logisticsData;
    },
    async getMerchantVoluntaryRefundByOrder(orderId: string) {
      const data = await fetchMerchantVoluntaryRefundByOrder(orderId);
      if (data) this.merchantVoluntaryRefundByOrder = data;
      return this.merchantVoluntaryRefundByOrder;
    },
    async updateListItemAndDetails(orderId: string) {
      // 更新列表数据和详情数据
      const detail = await this.getOrderDetails(orderId, true);
      const index = this.orderList.findIndex((it) => it.id === orderId);
      if (index >= 0 && this.orderList.length && detail) {
        this.orderList[index] = detail;
      }
    },
    /**
     * 赠品处理操作
     * @param promResult - 活动数据
     * @param allFreeGiftCommodity - 当前订单的所有赠品
     */
    freeGiftHandler(promResult: AnyObject, allFreeGiftCommodity: AnyObject[]) {
      // 获取赠品数据
      const gifts = promResult?.gifts ?? [];
      // 处理好的赠品列表
      let list: AnyObject[] = [];

      if (gifts.length && allFreeGiftCommodity?.length) {
        gifts.forEach((gift: AnyObject) => {
          if (gift.giftType === 'free') {
            gift?.items?.forEach((item: AnyObject) => {
              // 找到对应的赠品
              const target = allFreeGiftCommodity.find(
                (commodity: AnyObject) => commodity.id === item.giftId
              );

              // console.log(`找到对应的商品 target`, {
              //   allFreeGiftCommodity,
              //   gift,
              //   target,
              //   item,
              // });

              if (target) {
                item._commodityHandler = {
                  name: target.product.name,
                  sku: '请选择',
                  imgUrl: target.product.featuredAsset?.source,
                  amount: 0,
                  lineThroughAmount: target.product.customFields.markingPrice ?? 0,
                  count: item.count,
                };

                let variant: AnyObject | null = null;

                // 默认选中
                if (item.skuId && item.selected) {
                  // console.log(`默认选中 variant`, variant);
                  variant = target.product.variants.find((it: AnyObject) => it.id === item.skuId);
                } else {
                  // 如果没有默认，则选择规格的第一个
                  // console.log(`如果没有默认，则选择规格的第一个 variant`, variant);
                  variant = target.product.variants[0];
                }

                // 规格赋值
                if (variant) {
                  if (variant.featuredAsset?.source) {
                    item._commodityHandler.imgUrl = variant.featuredAsset?.source;
                  }
                  // 赠品价格显示0
                  item._commodityHandler.amount = 0;
                  // 赠品划线价显示赠品原价
                  item._commodityHandler.lineThroughAmount = variant.price;
                  item._commodityHandler.sku = businessUtils.sku.getText(variant.options);
                }

                //  缓存源数据
                item._source = target.product;
              }
            });

            list.push(gift);
          }
        });
      }

      // 没有赠品过滤
      list = list.filter((it) => !!it.items?.length);

      // 在活动数据里通过活动id比对，查找对应的活动类型
      list.forEach((it) => {
        const target = promResult?.promLineResults?.find(
          (promResult: AnyObject) => promResult.promInstanceId === it.promInstanceId
        );
        if (target) it.type = target.type;
      });

      // console.log(`赠品商品数据`, list);

      return list;
    },
  },
});
